import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  getBusyStatus,
  getCurrentSession,
  getHisSessionList,
  getSessionContent,
  getSessionContentDelta,
} from '@/api/chat'
import type { Message, SessionContentItem, ChatPendingState } from '@/types/chat'
import { resolveCurrentSessionAction } from '@/stores/currentSessionPolicy'
import { isNetworkError } from '@/utils'
import { tcShowToast, isApp } from '@/utils/bridge'
import { customStorage, getStorageItem } from '@/utils/storage'
import { findOptimisticUserMatch, hasContentId } from '@/views/Chat/chatSyncPolicy'

/**
 * AI 消息状态常量（与后端 qx_ai_session_content.status 对齐）
 * 新增于 20260511-ai-chat-resume（FR-03 / PRD §7.2）
 */
export const AiContentStatus = {
  COMPLETED: 1,
  PROCESSING: 2,
  FAILED: 3,
} as const

/** chat-store-pending 持久化 key */
const PENDING_STORAGE_KEY = 'chat-store-pending'
/** aiContentSoFar 截断上限：2KB（PRD §4.2 边界条件） */
const AI_CONTENT_SO_FAR_MAX_BYTES = 2048
/** pending 过期时长：25 分钟（与后端 Redis TTL 一致，PRD §4.3） */
const PENDING_EXPIRE_MS = 25 * 60 * 1000

/**
 * 截断字符串到指定字节上限（utf-16 char 视为最多 4 字节兜底）
 */
function truncateBytes(input: string, maxBytes: number): string {
  if (!input) return ''
  // 简单近似：用 length * 3（utf-8 字符上限 4 但中文常 3）；超出则按字符截
  if (input.length * 3 <= maxBytes) return input
  // 使用 TextEncoder 精确控制
  try {
    const encoder = new TextEncoder()
    const buf = encoder.encode(input)
    if (buf.byteLength <= maxBytes) return input
    // 二分查找最长可容纳字符前缀
    let lo = 0
    let hi = input.length
    while (lo < hi) {
      const mid = Math.floor((lo + hi + 1) / 2)
      if (encoder.encode(input.slice(0, mid)).byteLength <= maxBytes) {
        lo = mid
      } else {
        hi = mid - 1
      }
    }
    return input.slice(0, lo)
  } catch {
    // 没有 TextEncoder 的极端环境，退化按 1/3 估算
    return input.slice(0, Math.floor(maxBytes / 3))
  }
}

/**
 * 聊天状态管理
 */
export const useChatStore = defineStore('chat', () => {
  // ==================== 状态 ====================
  // 当前激活的 tab（0: 智能助手, 1: 账单, 2: 我的）
  const activeTab = ref(0)

  // 当前会话ID
  const sessionId = ref<string | number | null>(null)

  // RATE-05：同一 sessionId 内有效对话轮次（一条非空用户消息 + 一条完整成功 AI 回复 = 1 轮）。
  // 仅在当次访问内累计，切换会话或新建会话时重置；达到 3 轮时由聊天页上报好评候选。
  const validTurnCount = ref(0)

  // 消息列表
  const messages = ref<Message[]>([])
  const lastContentId = ref('0')
  const deltaInFlight = ref(false)
  const queuedCardMessages = ref<Message[]>([])
  const prefetchedTargetSessions = new Map<string, {
    messages: Message[]
    total: number
    nextPage: number
    finished: boolean
  }>()

  // 历史消息加载状态
  const historyLoading = ref(false) // 是否正在加载历史消息（包括首次加载和加载更多）
  const historyFinished = ref(false)
  const historyCurrent = ref(1)
  const historyTotal = ref(0)
  const isLoadingHistory = ref(false) // 是否正在首次加载历史消息（用于UI显示）

  // 常量
  const HISTORY_PAGE_SIZE = 30

  // ==================== 进行中对话持久化（chat-store-pending / FR-01）====================
  /** 内存中的 pending 状态镜像（fallback：customStorage 写失败时仅在内存中保留） */
  const pending = ref<ChatPendingState | null>(null)
  /** 是否已 hydrate 完成（避免读 storage 前误判为空） */
  const pendingHydrated = ref(false)

  /**
   * 从 storage 加载 pending（首次进入页面 / setup 阶段调用）
   */
  const hydratePending = async (): Promise<ChatPendingState | null> => {
    try {
      let raw: string | null = null
      if (isApp()) {
        raw = await getStorageItem(PENDING_STORAGE_KEY)
      } else {
        try {
          raw = sessionStorage.getItem(PENDING_STORAGE_KEY)
        } catch {
          raw = null
        }
      }
      if (raw) {
        const parsed = JSON.parse(raw) as ChatPendingState
        pending.value = parsed
      }
    } catch (e) {
      console.warn('[chatStore] hydratePending 解析失败:', e)
      pending.value = null
    } finally {
      pendingHydrated.value = true
    }
    return pending.value
  }

  /**
   * 写入 pending 到 storage（App: Native; H5: sessionStorage）
   * 失败时仅保留在内存 ref（PRD §4.2 异常分支约定）
   */
  const savePending = (payload: Partial<ChatPendingState> & { userMessage: string; placeholderMessageId: string }) => {
    const merged: ChatPendingState = {
      sessionId: payload.sessionId ?? pending.value?.sessionId ?? sessionId.value ?? null,
      messageId: payload.messageId ?? pending.value?.messageId ?? null,
      userMessage: payload.userMessage,
      aiContentSoFar: truncateBytes(payload.aiContentSoFar ?? '', AI_CONTENT_SO_FAR_MAX_BYTES),
      placeholderMessageId: payload.placeholderMessageId,
      createdAt: payload.createdAt ?? Date.now(),
      isStreaming: payload.isStreaming ?? true,
    }
    pending.value = merged
    const json = JSON.stringify(merged)
    try {
      if (isApp()) {
        // customStorage.setItem 内部已做 App bridge + localStorage 兜底
        customStorage.setItem(PENDING_STORAGE_KEY, json)
      } else {
        try {
          sessionStorage.setItem(PENDING_STORAGE_KEY, json)
        } catch (e) {
          // 隐私模式 / 容量满 → 仅保留内存
          console.warn('[chatStore] sessionStorage 写入失败，pending 仅内存保留:', e)
        }
      }
    } catch (e) {
      console.warn('[chatStore] savePending storage 写入异常，pending 仅内存保留:', e)
    }
  }

  /**
   * 清空 pending（成功重连 / 过期 / 用户重新发送时调用）
   */
  const clearPending = () => {
    pending.value = null
    try {
      if (isApp()) {
        customStorage.removeItem(PENDING_STORAGE_KEY)
      } else {
        try {
          sessionStorage.removeItem(PENDING_STORAGE_KEY)
        } catch {
          // ignore
        }
      }
    } catch (e) {
      console.warn('[chatStore] clearPending storage 清空异常:', e)
    }
  }

  /**
   * 获取 pending；若已过期（createdAt 距今 > 25min）自动清空并返回 null
   */
  const getPending = (): ChatPendingState | null => {
    const p = pending.value
    if (!p) return null
    if (Date.now() - p.createdAt > PENDING_EXPIRE_MS) {
      console.log('[chatStore] pending 已过期，自动清空, createdAt:', p.createdAt)
      clearPending()
      return null
    }
    return p
  }

  // ==================== 工具函数 ====================
  /**
   * 检查时间是否在24小时内
   */
  const isWithin24Hours = (timeStr: string): boolean => {
    if (!timeStr) return false

    const lastTime = new Date(timeStr).getTime()
    const now = Date.now()
    const diff = now - lastTime
    const hours24 = 24 * 60 * 60 * 1000

    const isWithin = diff <= hours24
    console.log('时间检查:', {
      最后更新: timeStr,
      距离现在: `${Math.floor(diff / 1000 / 60)} 分钟`,
      是否24小时内: isWithin,
    })

    return isWithin
  }

  /**
   * 将服务器返回的历史消息转换为前端消息格式
   * 20260511-ai-chat-resume：携带 status 字段供前端按策略渲染（FR-03）
   *
   * 20260528-fix：只有「列表最后一条 assistant」才允许 status=PROCESSING → isStreaming=true。
   * 场景：用户问 Q1 后立即追问 Q2，后端会停止对 Q1 的内容写入但不会改 Q1 的 status，
   * 导致 Q1 DB 行长期停在 status=2 → 每次拉历史都被渲染成"回复中"形成两条 loading 气泡。
   * 解决：非末尾的 PROCESSING 视为孤儿（被追问截断的旧问），按 COMPLETED 渲染（保留 content，不显 loading）。
   * 与 tryResumeFromProcessingHistory "只续传最后一条" 假设对齐，逻辑闭环。
   */
  const convertHistoryToMessages = (historyItems: SessionContentItem[]): Message[] => {
    // 找最后一条 assistant 的索引（只允许它续传/显示流式）
    let lastAssistantIdx = -1
    for (let i = historyItems.length - 1; i >= 0; i--) {
      if (historyItems[i].role === 'assistant') {
        lastAssistantIdx = i
        break
      }
    }
    return historyItems.map((item, idx) => {
      const status = item.status ?? AiContentStatus.COMPLETED
      const isTailAssistant = idx === lastAssistantIdx
      const contentId = String(item.contentId ?? item.id)
      return {
        id: `history-${contentId}`,
        role: item.role as 'user' | 'assistant',
        content: item.content,
        timestamp: new Date(item.createTime).getTime(),
        contentId,
        messageType: item.messageType === 'CARD' ? 'CARD' : 'TEXT',
        cardId: item.cardId == null ? undefined : String(item.cardId),
        renderTemplate: item.renderTemplate,
        liked: item.pointUp > 0,
        disliked: item.pointDown > 0,
        status,
        // 仅末尾 assistant 的 PROCESSING 标记为 streaming；非末尾的 PROCESSING 视为孤儿，按已完成展示
        isStreaming: isTailAssistant && status === AiContentStatus.PROCESSING,
      }
    })
  }

  // ==================== Actions ====================
  /**
   * 记录一轮有效对话并返回当前累计轮次（RATE-05）。
   * 调用方需保证仅在一轮「非空用户消息 + 完整成功 AI 回复」真正完成时调用。
   */
  const recordValidTurn = () => {
    validTurnCount.value += 1
    return validTurnCount.value
  }

  /**
   * 重置有效对话轮次（切换会话 / 新建会话时调用）。
   */
  const resetValidTurnCount = () => {
    validTurnCount.value = 0
  }

  /**
   * 设置会话ID
   *
   * <p>不变量保护（20260520 hotfix - 会话切换串流 Bug）：
   * 当检测到 sessionId 发生"真切换"（oldId 与 newId 均非 null 且不相等）时，
   * 自动清空 messages 并重置历史加载状态。理由：
   *  - vue-router 同路径 `/chat` 切 query 会复用 ChatBot.vue 实例，store 单例 messages 留存上一会话
   *  - loadHistoryMessages 的合并去重逻辑在 streamingIds / pairedUserIds 命中时会保留旧会话消息
   *  - 让"切会话清 messages"成为 store 自身语义，任何调用方都不必手动清</p>
   *
   * <p>例外情况：
   *  - 首次设置（oldId 为 null）不清，避免破坏首次进入加载历史
   *  - 设置为 null（如新建对话场景）不清，由 resetToNewSession 等其他 action 统一负责</p>
   */
  const setSessionId = (id: string | number | null) => {
    const oldId = sessionId.value
    if (oldId !== null && id !== null && String(oldId) !== String(id)) {
      clearMessages()
      resetHistoryState()
      resetValidTurnCount()
    }
    sessionId.value = id
  }

  /**
   * 添加消息（同 id 已存在时跳过，避免重复 push）
   */
  const addMessage = (message: Message) => {
    if (messages.value.some((m) => m.id === message.id
      || (message.contentId && String(m.contentId) === String(message.contentId)))) {
      return
    }
    messages.value.push(message)
  }

  /**
   * 更新消息
   */
  const updateMessage = (messageId: string, updates: Partial<Message>) => {
    const index = messages.value.findIndex((m) => m.id === messageId)
    if (index !== -1) {
      messages.value[index] = { ...messages.value[index], ...updates }
    }
  }

  /**
   * 删除消息
   */
  const removeMessage = (messageId: string) => {
    const index = messages.value.findIndex((m) => m.id === messageId)
    if (index !== -1) {
      messages.value.splice(index, 1)
    }
  }

  /**
   * 清除"历史快照僵尸占位"（20260519 hotfix）
   *
   * 场景：切走切回触发 loadHistoryMessages 时，DB status=2 的 ai 会被 convertHistoryToMessages
   * 转为 id=`history-${contentId}` + isStreaming=true 的占位。SSE 后续在前端原创的
   * ai 消息上完成（id=msg_yyy），但这个 history- 僵尸占位永远没人维护，
   * 留在 messages 里导致下一次 loadHistoryMessages 误识别为"前端正在流"→ 误救前一条 user → 累加。
   *
   * SSE done 回调中调用此方法，清掉所有 id 以 'history-' 开头且 isStreaming=true 的占位。
   */
  const removeStaleHistoryStreamingPlaceholders = () => {
    messages.value = messages.value.filter(
      (m) => !(typeof m.id === 'string' && m.id.startsWith('history-') && m.isStreaming === true)
    )
  }

  /**
   * 清空消息列表
   */
  const clearMessages = () => {
    messages.value = []
  }

  /**
   * 重置历史加载状态
   */
  const resetHistoryState = () => {
    historyLoading.value = false
    historyFinished.value = false
    historyCurrent.value = 1
    historyTotal.value = 0
    isLoadingHistory.value = false
  }

  /**
   * 重置为新对话状态
   */
  const resetToNewSession = () => {
    sessionId.value = null
    messages.value = []
    resetHistoryState()
    resetValidTurnCount()
    lastContentId.value = '0'
    queuedCardMessages.value = []
  }

  const toDeltaMessage = (item: SessionContentItem): Message => {
    const contentId = String(item.contentId ?? item.id)
    return {
      id: `delta-${contentId}`,
      role: item.role === 'user' ? 'user' : 'assistant',
      content: item.content || '',
      timestamp: new Date(item.createTime).getTime(),
      contentId,
      status: item.status,
      liked: (item.pointUp || 0) > 0,
      disliked: (item.pointDown || 0) > 0,
      messageType: item.messageType === 'CARD' ? 'CARD' : 'TEXT',
      cardId: item.cardId == null ? undefined : String(item.cardId),
      renderTemplate: item.renderTemplate,
    }
  }

  const reconcileOptimisticUserMessage = (message: Message): boolean => {
    if (message.role !== 'user' || !message.contentId) return false
    const index = findOptimisticUserMatch(messages.value, message)
    const candidate = index >= 0 ? messages.value[index] : undefined
    if (!candidate) return false
    messages.value[index] = { ...candidate, contentId: message.contentId, status: message.status }
    return true
  }

  const flushQueuedCards = (): Message[] => {
    const appended: Message[] = []
    const queued = queuedCardMessages.value.splice(0)
    for (const message of queued) {
      const before = messages.value.length
      addMessage(message)
      if (messages.value.length > before) appended.push(message)
    }
    return appended
  }

  const syncDelta = async (queueCards = false): Promise<Message[]> => {
    if (!sessionId.value || deltaInFlight.value) return []
    deltaInFlight.value = true
    const appended: Message[] = []
    try {
      let hasMore = true
      let page = 0
      while (hasMore && page < 20) {
        page++
        const previousCursor = lastContentId.value
        const data = await getSessionContentDelta({
          sessionId: String(sessionId.value),
          lastContentId: lastContentId.value,
          limit: 50,
        })
        for (const item of data.records || []) {
          const message = toDeltaMessage(item)
          if (hasContentId(messages.value, message.contentId)
              || hasContentId(queuedCardMessages.value, message.contentId)) continue
          if (reconcileOptimisticUserMessage(message)) continue
          if (queueCards && message.messageType === 'CARD') {
            queuedCardMessages.value.push(message)
          } else {
            addMessage(message)
            appended.push(message)
          }
        }
        lastContentId.value = String(data.nextLastContentId || lastContentId.value)
        hasMore = Boolean(data.hasMore) && lastContentId.value !== previousCursor
      }
      return appended
    } finally {
      deltaInFlight.value = false
    }
  }

  const prefetchTargetSession = async (targetSessionId: string, targetContentId: string) => {
    const key = String(targetSessionId)
    const prefetched: Message[] = []
    let current = 1
    let total = 0
    let found = false

    while (current <= 100 && !found) {
      const data = await getSessionContent({
        current,
        size: HISTORY_PAGE_SIZE,
        sessionId: key,
      })
      const records = data.records || []
      total = Number(data.total || 0)
      if (!records.length) break
      const pageMessages = convertHistoryToMessages(records)
      prefetched.unshift(...pageMessages)
      found = pageMessages.some((message) => String(message.contentId || '') === targetContentId)
      if (current * HISTORY_PAGE_SIZE >= total) break
      current++
    }

    if (!found) return false
    prefetchedTargetSessions.set(key, {
      messages: prefetched,
      total,
      nextPage: current + 1,
      finished: prefetched.length >= total,
    })
    return true
  }

  const consumePrefetchedTargetSession = (targetSessionId: string) => {
    const key = String(targetSessionId)
    const prefetched = prefetchedTargetSessions.get(key)
    if (!prefetched) return false
    prefetchedTargetSessions.delete(key)
    sessionId.value = key
    messages.value = prefetched.messages
    historyTotal.value = prefetched.total
    historyCurrent.value = prefetched.nextPage
    historyFinished.value = prefetched.finished
    historyLoading.value = false
    isLoadingHistory.value = false
    lastContentId.value = prefetched.messages.reduce((max, message) => {
      const id = message.contentId && /^\d+$/.test(message.contentId) ? BigInt(message.contentId) : 0n
      return id > max ? id : max
    }, 0n).toString()
    return true
  }

  const refreshCurrentSession = async (): Promise<
    'created' | 'loaded' | 'retained' | 'missing' | 'load-failed'
  > => {
    const current = await getCurrentSession()
    if (!current?.sessionId) return 'missing'

    const nextSessionId = String(current.sessionId)
    const action = resolveCurrentSessionAction({
      localSessionId: String(sessionId.value || ''),
      currentSessionId: nextSessionId,
      created: current.created,
      messageCount: messages.value.length,
    })

    if (action === 'created') {
      clearPending()
      resetToNewSession()
      setSessionId(nextSessionId)
      return 'created'
    }

    if (action === 'retained') {
      return 'retained'
    }

    setSessionId(nextSessionId)
    resetHistoryState()
    const loaded = await loadHistoryMessages(true)
    if (!loaded) return 'load-failed'

    const maxContentId = messages.value.reduce((max, message) => {
      const id = message.contentId && /^\d+$/.test(message.contentId) ? BigInt(message.contentId) : 0n
      return id > max ? id : max
    }, 0n)
    lastContentId.value = maxContentId.toString()
    return 'loaded'
  }

  const loadCurrentSession = async (): Promise<boolean> => {
    const result = await refreshCurrentSession()
    return result === 'loaded' || result === 'retained'
  }

  /**
   * 加载历史会话内容
   */
  const loadHistoryMessages = async (isFirstLoad = false) => {
    if (!sessionId.value || historyLoading.value || historyFinished.value) return

    historyLoading.value = true
    if (isFirstLoad) {
      isLoadingHistory.value = true
    }

    try {
      const data = await getSessionContent({
        current: historyCurrent.value,
        size: HISTORY_PAGE_SIZE,
        sessionId: sessionId.value,
      })

      if (data.records && data.records.length > 0) {
        historyTotal.value = data.total

        // 转换历史消息格式
        const historyMessages = convertHistoryToMessages(data.records)

        if (isFirstLoad) {
          // 首次加载：以 contentId 为合并键去重，保留前端正在流式的消息
          // 场景：
          //  - AI 流式中切走切回 → 前端 msg_xxx.isStreaming=true（cleanupSSE 不再强制改 false），
          //    保留它（用户能继续看到实时输出），丢弃 DB 中 status=2 的占位
          //  - AI 已完成切走切回 → 前端 msg_xxx.contentId 已被 done 事件设置，DB 中
          //    history-xxx.contentId 同源，丢弃前端版本，只显示 DB 版本（避免重复）
          const historyContentIds = new Set(
            historyMessages
              .filter((m) => m.contentId !== undefined && m.contentId !== null)
              .map((m) => String(m.contentId))
          )
          // 前端正在流式的消息 (user + assistant 一对) 的 contentId 集合
          // 用来在 DB 历史中剔除与之对应的占位 (DB status=2)
          const streamingContentIds = new Set<string>()
          for (const m of messages.value) {
            if (m.isStreaming && m.contentId) {
              streamingContentIds.add(String(m.contentId))
            }
          }
          // 第一步：DB 历史中剔除"前端正在流式"对应的 AI 占位 + 同步剔除其前一条 user
          // 20260519 hotfix：前端原创 user_msg_xxx 天生无 contentId（sendMessage 创建时），
          // 之前仅按 contentId 剔除 AI 占位会保留 history-user_dbId，与前端 user_msg_xxx 共存形成重复气泡；
          // 这里把 AI 占位的前一条 user（同一对话回合）一并剔除，避免重复。
          const skipHistoryIdx = new Set<number>()
          historyMessages.forEach((hm, idx) => {
            const cid = hm.contentId !== undefined && hm.contentId !== null ? String(hm.contentId) : null
            if (cid && streamingContentIds.has(cid)) {
              skipHistoryIdx.add(idx)
              const prev = idx > 0 ? historyMessages[idx - 1] : undefined
              if (prev && prev.role === 'user') {
                skipHistoryIdx.add(idx - 1)
              }
            }
          })
          // 20260519 hotfix2：兜底剔除"无 contentId 的 streaming 对应 history 末尾对"
          // 触发场景：用户在 SSE 启动后、session 事件到达前（500ms 窗口）切走 tab
          //   → 前端 ai_yyy.isStreaming=true 但 contentId 未设
          //   → streamingContentIds 空 → 上面的 contentId 剔除逻辑失效
          //   → history-user + history-ai 完整保留 + 前端 user_msg_xxx + ai_yyy 也保留 → 2 对幽灵气泡
          // 修复：若前端有"isStreaming=true 但无 contentId"的 assistant，
          //   倒着找 history 末尾第一条 status=PROCESSING(=2) 的 ai 占位 + 其前一条 user，强制剔除
          const hasStreamingWithoutContentId = messages.value.some(
            (m) => m.role === 'assistant' && m.isStreaming === true && !m.contentId
          )
          if (hasStreamingWithoutContentId) {
            for (let i = historyMessages.length - 1; i >= 0; i--) {
              const hm = historyMessages[i]
              if (hm && hm.role === 'assistant' && hm.status === AiContentStatus.PROCESSING) {
                skipHistoryIdx.add(i)
                const prev = i > 0 ? historyMessages[i - 1] : undefined
                if (prev && prev.role === 'user') {
                  skipHistoryIdx.add(i - 1)
                }
                break
              }
            }
          }
          const filteredHistory = historyMessages.filter((_, idx) => !skipHistoryIdx.has(idx))
          // 第二步：前端 messages 中保留"正在流式" 或与之配对的 user 消息
          // 先收集正在流式的 AI 消息 id，再保留其前一条 user 消息
          const streamingIds = new Set(
            messages.value.filter((m) => m.isStreaming).map((m) => m.id)
          )
          const pairedUserIds = new Set<string>()
          for (let i = 0; i < messages.value.length; i++) {
            const m = messages.value[i]
            if (m.isStreaming && i > 0 && messages.value[i - 1].role === 'user') {
              pairedUserIds.add(messages.value[i - 1].id)
            }
          }
          const survivingFrontEnd = messages.value.filter((m) => {
            if (streamingIds.has(m.id)) return true
            if (pairedUserIds.has(m.id)) return true
            const cid = m.contentId !== undefined && m.contentId !== null ? String(m.contentId) : null
            if (!cid) return false
            if (historyContentIds.has(cid)) return false
            return true
          })
          messages.value = [...filteredHistory, ...survivingFrontEnd]
        } else {
          // 加载更多：插入到消息列表顶部
          messages.value.unshift(...historyMessages)
        }

        // 判断是否还有更多数据
        if (messages.value.length >= data.total) {
          historyFinished.value = true
        } else {
          historyCurrent.value++
        }
      } else {
        historyFinished.value = true
      }

      return true
    } catch (error: unknown) {
      console.error('加载历史消息失败:', error)

      if (isNetworkError(error)) {
        tcShowToast('网络异常，请检查后重试')
      } else {
        tcShowToast('加载失败，请稍后重试')
      }

      // 首次加载失败时才标记为完成
      if (historyCurrent.value === 1) {
        historyFinished.value = true
      }

      return false
    } finally {
      historyLoading.value = false
      if (isFirstLoad) {
        isLoadingHistory.value = false
      }
    }
  }

  /**
   * 检查并加载24小时内的最近对话
   */
  const checkAndLoadRecentSession = async () => {
    try {
      console.log('检查是否有24小时内的对话...')

      // 获取最近的一条会话记录
      const data = await getHisSessionList({
        current: 1,
        size: 1,
      })

      if (!data.records || data.records.length === 0) {
        console.log('没有历史会话记录')
        return false
      }

      const latestSession = data.records[0]
      const updateTime = latestSession.lastMessageTime || latestSession.updateTime

      console.log('最近的会话:', {
        sessionId: latestSession.sessionId,
        title: latestSession.sessionTittle || latestSession.sessionTitle,
        updateTime,
      })

      // 检查是否在24小时内
      if (isWithin24Hours(updateTime)) {
        console.log('在24小时内，加载历史对话')
        sessionId.value = latestSession.sessionId
        await loadHistoryMessages(true)
        return true
      } else {
        console.log('不在24小时内，不加载历史对话')
        return false
      }
    } catch (error: unknown) {
      console.error('检查最近会话失败:', error)
      return false
    }
  }

  /**
   * 从最新历史会话同步 sessionId
   */
  const fetchLatestSessionId = async () => {
    try {
      console.log('从历史会话同步 sessionId...')
      const data = await getHisSessionList({
        current: 1,
        size: 1,
      })

      if (data.records && data.records.length > 0) {
        const latestSessionId = data.records[0].sessionId
        sessionId.value = latestSessionId
        console.log('从历史会话同步 sessionId:', latestSessionId)
      }
    } catch (error) {
      console.error('同步 sessionId 失败:', error)
    }
  }

  /**
   * 清除所有聊天数据(用于退出登录/注销账号)
   */
  const clearAllChatData = () => {
    sessionId.value = null
    messages.value = []
    activeTab.value = 0
    historyLoading.value = false
    historyFinished.value = false
    historyCurrent.value = 1
    historyTotal.value = 0
    isLoadingHistory.value = false
    // 同时清空 pending（20260511-ai-chat-resume）
    clearPending()
    // 退登清理 BUSY 轮询（20260519-ai-chat-busy-block）
    stopBusyPolling()
  }

  // ==================== BUSY 轮询（20260519-ai-chat-busy-block / FR-04 + FR-05）====================
  /** 当前用户是否处于 AI 回答中（true=置灰输入框 + 锁住发送按钮） */
  const isBusy = ref(false)
  /** 轮询定时器（setInterval 句柄） */
  const busyPollingTimer = ref<ReturnType<typeof setInterval> | null>(null)
  /** 轮询起始时间戳（用于 120s 上限检测） */
  const busyPollingStartAt = ref<number | null>(null)

  /** 轮询间隔（PRD §6.2 业务规则 5：2s） */
  const BUSY_POLL_INTERVAL_MS = 2000
  /** 轮询上限（PRD §6.2 业务规则 5 / AC-07：120s） */
  const BUSY_POLL_TIMEOUT_MS = 120 * 1000

  /**
   * 停止 BUSY 轮询并解除忙态（清理 setInterval + 重置标志）。
   *
   * <p>3 个清理点之一（其余 2 个：onUnmounted / clearAllChatData）。</p>
   */
  function stopBusyPolling() {
    if (busyPollingTimer.value) {
      clearInterval(busyPollingTimer.value)
      busyPollingTimer.value = null
    }
    isBusy.value = false
    busyPollingStartAt.value = null
  }

  /**
   * 进入忙态：开启 2s 轮询 + 置 isBusy=true。
   *
   * <p>幂等：已在轮询时直接 return，避免重复 setInterval。</p>
   * <p>120s 上限：超时 toast 后停止轮询（PRD AC-07）。</p>
   * <p>busy=false 自然解除（不自动重发，PRD §6.2 业务规则 4 / AC-04）。</p>
   */
  function startBusyPolling() {
    if (busyPollingTimer.value) return // 幂等
    isBusy.value = true
    busyPollingStartAt.value = Date.now()
    busyPollingTimer.value = setInterval(async () => {
      // 120s 上限检测
      if (busyPollingStartAt.value && Date.now() - busyPollingStartAt.value > BUSY_POLL_TIMEOUT_MS) {
        tcShowToast('AI 响应超时，请稍后重试')
        stopBusyPolling()
        return
      }
      try {
        const resp = await getBusyStatus()
        if (!resp || !resp.busy) {
          // busy=false 自然解除（不自动重发）
          stopBusyPolling()
        }
      } catch (e) {
        // 5xx / 网络异常不立即终止（用户可能希望继续等），仅 warn
        console.warn('[chatStore] busy-status 轮询失败:', e)
      }
    }, BUSY_POLL_INTERVAL_MS)
  }

  return {
    // 状态
    activeTab,
    sessionId,
    validTurnCount,
    messages,
    lastContentId,
    deltaInFlight,
    queuedCardMessages,
    historyLoading,
    historyFinished,
    historyCurrent,
    historyTotal,
    isLoadingHistory,
    pending,
    pendingHydrated,
    // BUSY 状态（20260519-ai-chat-busy-block）
    isBusy,
    busyPollingTimer,
    busyPollingStartAt,

    // Actions
    setSessionId,
    recordValidTurn,
    resetValidTurnCount,
    addMessage,
    updateMessage,
    removeMessage,
    removeStaleHistoryStreamingPlaceholders,
    clearMessages,
    resetHistoryState,
    resetToNewSession,
    loadHistoryMessages,
    loadCurrentSession,
    refreshCurrentSession,
    syncDelta,
    prefetchTargetSession,
    consumePrefetchedTargetSession,
    flushQueuedCards,
    checkAndLoadRecentSession,
    fetchLatestSessionId,
    clearAllChatData,
    // pending actions（20260511-ai-chat-resume / FR-01）
    hydratePending,
    savePending,
    clearPending,
    getPending,
    // BUSY 轮询 actions（20260519-ai-chat-busy-block / FR-04+FR-05）
    startBusyPolling,
    stopBusyPolling,
  }
},
  // 持久化配置（仅 App 环境启用）
  // H5 是 SPA，store 数据在内存中，页面跳转不会丢失，无需持久化
  // App 每个页面是独立 WebView，store 数据不共享，需要持久化到 Native
  isApp() ? {
    persist: {
      key: 'chat-store',
      storage: customStorage,
      // 只持久化 sessionId（消息列表不持久化，太大且需要重新获取）
      // activeTab 不需要持久化，每次进入页面默认重置为 0
      paths: ['sessionId'],
      async beforeHydrate(ctx) {
        try {
          const savedState = await getStorageItem('chat-store')
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.sessionId !== undefined) ctx.store.sessionId = state.sessionId
            // if (state.activeTab !== undefined) ctx.store.activeTab = state.activeTab
          }
        } catch (error) {
          console.error('从 Native 加载 chatStore 数据失败:', error)
        }
      },
      // 合并策略：只合并 sessionId，其他状态保持默认值
      merge: (defaultState, savedState) => {
        if (savedState && typeof savedState === 'object') {
          return {
            ...defaultState,
            sessionId: (savedState as Record<string, unknown>).sessionId ?? defaultState.sessionId,
          }
        }
        return defaultState
      },
    },
  } : {}
)
